import { getChainConfigEntry } from '@/config/chain'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import type { IsolatedUserData, PairDetails } from '@/features/isolated/logic/useIsolatedPairs'
import { formatUnits } from 'viem'
import type { IsolatedSourceData, YieldExposure, YieldOpportunity } from '../types'
import { makeYieldOpportunityId } from './makeYieldOpportunityId'

interface IsolatedPairWithUserData extends PairDetails {
  chainId: number
  userAssetShares?: bigint
  userCollateralBalance?: bigint
  walletBalance?: NormalizedUnitNumber
}

function computeIsolatedUserDeposit(pair: IsolatedPairWithUserData): {
  userDeposit: NormalizedUnitNumber | undefined
  userDepositUsd: NormalizedUnitNumber
} | undefined {
  const hasSupply = pair.userAssetShares && pair.userAssetShares > 0n && pair.totalAssetShares > 0n
  const hasCollateral = pair.userCollateralBalance && pair.userCollateralBalance > 0n

  if (!hasSupply && !hasCollateral) return undefined

  let totalUsd = 0
  let supplyAmount: NormalizedUnitNumber | undefined

  if (hasSupply) {
    const userAssetAmount = (pair.userAssetShares! * pair.totalAssetAmount) / pair.totalAssetShares
    const formatted = formatUnits(userAssetAmount, pair.borrowToken.decimals)
    supplyAmount = NormalizedUnitNumber(formatted)
    totalUsd += Number(formatted) * Number(pair.borrowToken.unitPriceUsd)
  }

  if (hasCollateral) {
    const formatted = formatUnits(pair.userCollateralBalance!, pair.collateralToken.decimals)
    totalUsd += Number(formatted) * Number(pair.collateralToken.unitPriceUsd)
  }

  if (totalUsd <= 0) return undefined

  return {
    userDeposit: supplyAmount,
    userDepositUsd: NormalizedUnitNumber(totalUsd),
  }
}

export function mapIsolatedPairToYieldOpportunity(pair: IsolatedPairWithUserData): YieldOpportunity {
  const { chainId, address } = pair
  const assetSymbol = pair.borrowToken.symbol.toString()

  const hasPosition =
    (pair.userAssetShares !== undefined && pair.userAssetShares > 0n) ||
    (pair.userCollateralBalance !== undefined && pair.userCollateralBalance > 0n)
  const depositData = computeIsolatedUserDeposit(pair)

  const tvlUsd = calculateIsolatedTvlUsd(pair)
  const totalAssets = calculateIsolatedTotalAssets(pair)
  const totalBorrowedUsd = calculateIsolatedBorrowedUsd(pair)

  const { isolatedPartnerPoints } = getChainConfigEntry(chainId)
  const partnerPoints = isolatedPartnerPoints[CheckedAddress(address)]?.points

  const sourceData: IsolatedSourceData = {
    type: 'isolated',
    collateralToken: pair.collateralToken,
    borrowToken: pair.borrowToken,
    utilization: pair.utilization,
    borrowAPY: pair.borrowAPY,
    maxLtv: pair.maxLtv,
    totalBorrowed: totalBorrowedUsd,
    totalAssets,
  }

  const exposure: YieldExposure = {
    type: 'isolated-pair',
    isolatedPair: {
      collateralToken: pair.collateralToken,
      borrowToken: pair.borrowToken,
      maxLtv: pair.maxLtv,
    },
  }

  return {
    id: makeYieldOpportunityId('isolated', chainId, address),
    source: 'isolated',
    chainId,
    address,
    name: `${pair.collateralToken.symbol}/${pair.borrowToken.symbol}`,
    symbol: pair.borrowToken.symbol.toString(),
    assetSymbol,
    tokenInfo: undefined,
    token: pair.borrowToken,
    supplyAPY: pair.lendAPY,
    tvlUsd,
    walletBalance: pair.walletBalance,
    userDeposit: depositData?.userDeposit,
    userDepositUsd: depositData?.userDepositUsd,
    hasPosition,
    curatorName: 'HypurrFi',
    curatorLogo: '/hypurr-paw-no-shadow.svg',
    market: undefined,
    points: undefined,
    partnerPoints,
    exposure,
    utilization: pair.utilization / 100, // Convert from percentage (0-100) to decimal (0-1)
    sourceData,
    fraxlendPauseState: pair.fraxlendPauseState,
  }
}

function calculateIsolatedTotalAssets(pair: PairDetails): NormalizedUnitNumber | undefined {
  const totalBorrowNum = Number(pair.totalBorrow) / 10 ** pair.borrowToken.decimals
  const availableNum = Number(pair.available) / 10 ** pair.borrowToken.decimals
  const totalDeposited = totalBorrowNum + availableNum

  if (totalDeposited <= 0) return undefined
  return NormalizedUnitNumber(totalDeposited.toString())
}

function calculateIsolatedTvlUsd(pair: PairDetails): NormalizedUnitNumber | undefined {
  const borrowTokenPrice = Number(pair.borrowToken.unitPriceUsd)
  if (borrowTokenPrice <= 0) return undefined

  const totalBorrowNum = Number(pair.totalBorrow) / 10 ** pair.borrowToken.decimals
  const availableNum = Number(pair.available) / 10 ** pair.borrowToken.decimals
  const totalDeposited = totalBorrowNum + availableNum

  return NormalizedUnitNumber((totalDeposited * borrowTokenPrice).toString())
}

function calculateIsolatedBorrowedUsd(pair: PairDetails): NormalizedUnitNumber | undefined {
  const borrowTokenPrice = Number(pair.borrowToken.unitPriceUsd)
  if (borrowTokenPrice <= 0) return undefined

  const totalBorrowNum = Number(pair.totalBorrow) / 10 ** pair.borrowToken.decimals
  return NormalizedUnitNumber((totalBorrowNum * borrowTokenPrice).toString())
}

export function mapIsolatedPairsToYieldOpportunities(
  pairs: PairDetails[],
  chainId: number,
  userDataMap?: Map<string, IsolatedUserData>,
): YieldOpportunity[] {
  return pairs.map((pair) => {
    const userData = userDataMap?.get(pair.address.toLowerCase())
    return mapIsolatedPairToYieldOpportunity({
      ...pair,
      chainId,
      userAssetShares: userData?.userAssetShares,
      userCollateralBalance: userData?.userCollateralBalance,
      walletBalance: userData?.walletBalance,
    })
  })
}
